// @ts-strict-ignore
import {MPContext} from 'common/mp-context';
import {FeatureGate} from 'common/report/constants';
import {isAllowedSuperadminOverride} from 'common/report/util/saved-entities';
import {generatePermalink} from 'common/report/util/insights-permalink';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {getLocation} from 'common/util/url';
import {PropertyDefinition} from 'apps/lexicon/src/models/property-definition';
import {GenerateParamsQueryShowClause} from 'common/report/util/types';
import {DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {SharableEntityType} from 'common/report/util/saved-entities/types';
import {EventDefinition} from 'apps/lexicon/src/models';
import {isDataVolumeMonitoringEnabled} from 'common/report/util/feature-sampling';
import {FeatureFlag, ProjectFeatureFlag} from 'common/report/constants';

import {LexiconDefinition, LexiconEntitySpecialCharEscapeMap} from '../types';

function isEventDefinition(definition: LexiconDefinition, type?: SharableEntityType): definition is EventDefinition {
  return definition && (definition[`customEvent`] || type === SharableEntityType.CustomEvent);
}

function isPropertyDefinition(
  definition: LexiconDefinition,
  type?: SharableEntityType,
): definition is PropertyDefinition {
  return definition && (definition[`customPropertyId`] || type === SharableEntityType.CustomProperty);
}

// Shared gate for the new merge/unmerge endpoints across events and properties.
export function isImproveMergeEnabled(): boolean {
  return Boolean(
    mixpanel.flags?.get_variant_value_sync(FeatureFlag.ImproveMerge, false) ||
      mixpanel.flags?.get_variant_value_sync(FeatureFlag.ImproveMergeProd, false),
  );
}

export function isDataVolumeMonitoringV2Enabled(currentProjectStore) {
  return (
    currentProjectStore?.value &&
    isDataVolumeMonitoringEnabled(currentProjectStore?.value) &&
    currentProjectStore.value.hasFeatureFlag(ProjectFeatureFlag.DataVolumeMonitoringV2)
  );
}

export const lexiconUtils = {
  isDefinitionEditable,
  hasHideFeatureEnabled,
};

export function isDefinitionEditable(hasWritePermissions, definition?: LexiconDefinition) {
  // use can_update_basic for custom events and custom properties
  if (isEventDefinition(definition)) {
    return (
      definition?.customEvent?.canUpdateBasic ||
      definition?.canUpdateBasic ||
      isAllowedSuperadminOverride(definition?.customEvent) ||
      isAllowedSuperadminOverride(definition)
    );
  } else if (isPropertyDefinition(definition)) {
    return definition?.canUpdateBasic || isAllowedSuperadminOverride(definition);
  } else if (definition && `can_update_basic` in definition) {
    return definition.can_update_basic;
  }

  return hasWritePermissions;
}

function hasHideFeatureEnabled(context: MPContext) {
  return context.hasFeatureGate(FeatureGate.CanHideEventsAndProperties);
}

const EVENT_STATUS_FILTER_NAME = `eventStatus`;
const PROPERTY_STATUS_FILTER_NAME = `propertyStatus`;

export function getSpecialValuesFromUrl(): {
  eventStatusFilterValue: string;
  propertyStatusFilterValue: string;
  mpSource: string;
} {
  const currentUrl = new URL(getLocation().href);

  const eventStatusFilterValue = currentUrl.searchParams.get(EVENT_STATUS_FILTER_NAME);
  const propertyStatusFilterValue = currentUrl.searchParams.get(PROPERTY_STATUS_FILTER_NAME);
  const mpSource = currentUrl.searchParams.get(`mp_source`);

  return {eventStatusFilterValue, propertyStatusFilterValue, mpSource};
}

export function getReportLinkUrl(showClause: GenerateParamsQueryShowClause, reportType = `insights`) {
  return generatePermalink(
    {
      projectId: getProjectId(),
      workspaceId: getWorkspaceId(),
      query: {
        dateRange: {
          type: DateRangeType.RelativeAfter,
          window: {
            unit: TimeUnit.Day,
            value: 30,
          },
        },
        showClauses: [showClause],
      },
    },
    false,
    reportType,
  );
}

/** Will get a very specific report URL with Anomaly Detection on for a single event name. */
export function getForecastInsightsReportLinkUrl({
  eventName,
  propertyName,
}: {
  eventName: string;
  propertyName?: string;
}) {
  const showClause = {
    showEvent: {
      event: eventName,
    },
  };

  const reportType = `insights`;
  const forecastSlug = btoa(`forecastEvent:${eventName}`);
  return generatePermalink(
    {
      projectId: getProjectId(),
      workspaceId: getWorkspaceId(),
      query: {
        dateRange: {
          type: DateRangeType.RelativeAfter,
          window: {
            unit: TimeUnit.Day,
            value: 30,
          },
        },
        showClauses: [showClause],
        propertyFilters: propertyName
          ? [
              {
                property: {
                  name: propertyName,
                  source: `EVENTS`,
                  typecast: `STRING`,
                },
                stringFilter: {
                  operator: `EQUALS`,
                  operands: [`undefined`, ``],
                },
              },
            ]
          : [],
      },
      queryParams: {forecast: forecastSlug},
    },
    false,
    reportType,
  );
}

/**
 * convert string to all lowercase and replace white space into dash (-)
 */
export function getLowercaseKebabCase(text: string): string {
  return text.toLocaleLowerCase().replace(/\s/g, `-`);
}

// These functions are used to escape/unescape special characters that break React Router/Django for entity names that are used in URLs
export function getEscapedEntityName(entityName?: string): string {
  if (!entityName) {
    return entityName;
  }
  let replacedName = entityName;
  Object.entries(LexiconEntitySpecialCharEscapeMap).forEach(([char, escapeStr]) => {
    replacedName = replacedName.replace(new RegExp(`\\${char}`, `g`), escapeStr);
  });
  return replacedName;
}

export function getUnescapedEntityName(entityName?: string): string {
  if (!entityName) {
    return entityName;
  }
  let replacedName = entityName;
  Object.entries(LexiconEntitySpecialCharEscapeMap).forEach(([char, escapeStr]) => {
    replacedName = replacedName.replace(escapeStr, char);
  });
  return replacedName;
}
